import { type WritableAtom } from 'jotai';
// eslint-disable-next-line no-restricted-imports -- the one sanctioned import: this module IS the guard every other file is pointed at
import { atomWithHash as atomWithHashUnguarded } from 'jotai-location';
import { type RESET } from 'jotai/utils';

// Mirrors jotai-location's (unexported) SetStateActionWithReset.
type SetStateActionWithReset<Value> =
  | Value
  | typeof RESET
  | ((prev: Value) => Value | typeof RESET);

type SetHashOption = 'default' | 'replaceState' | ((searchParams: string) => void);

export type AtomWithHashOptions<Value> = {
  serialize?: (val: Value) => string;
  deserialize?: (str: string) => Value;
  subscribe?: (callback: () => void) => () => void;
  setHash?: SetHashOption;
};

/**
 * Drop-in `atomWithHash` whose URL write cannot crash React.
 *
 * STOP-GAP for an upstream Intercom bug, reported to Intercom support: their
 * messenger `frame.js` wraps `history.pushState`/`replaceState` (gtag, the
 * Facebook pixel and Clarity wrap them too) and reads `parent.document`
 * without a null check, so it throws once its iframe is torn down (`can't
 * access property "document", parent is null`).
 * jotai-location updates the atom's state *before* writing the hash, so
 * swallowing that throw loses only the URL fragment update; letting it
 * propagate crashed the whole tree to the app error boundary mid-commit.
 * Once Intercom ships a null-guard this wrapper becomes harmless belt-and-
 * braces — keep it, but it no longer needs to grow.
 *
 * The one-arg write tuple is deliberate: jotai-location's own type also
 * accepts a per-write `{ setHash }` override, which would route around this
 * guard — narrowing the annotation makes that override fail to compile.
 */
export const atomWithHash = <Value>(
  key: string,
  initialValue: Value,
  options?: AtomWithHashOptions<Value>
): WritableAtom<Value, [SetStateActionWithReset<Value>], void> =>
  atomWithHashUnguarded<Value>(key, initialValue, {
    ...options,
    setHash: guardSetHash(options?.setHash),
  });

const guardSetHash = (setHash: SetHashOption | undefined) => {
  const write = typeof setHash === 'function' ? setHash : buildHistoryWrite(setHash);
  return (searchParams: string) => {
    try {
      write(searchParams);
    } catch (error) {
      console.warn(
        '[atomWithHash] URL hash update failed (a third-party history wrapper threw); atom state is unaffected.',
        error
      );
    }
  };
};

// Mirrors jotai-location's setHashWithPush / setHashWithReplace (verified
// against 0.6.1), which are not exported from the package root. The default
// mode must be reimplemented here too — Intercom wraps pushState as well, so
// delegating it to the library would leave default-mode atoms unguarded. If a
// jotai-location bump changes these semantics, this copy must follow.
const buildHistoryWrite =
  (setHash: 'default' | 'replaceState' | undefined) => (searchParams: string) => {
    // During client-side route transitions Next.js may momentarily leave
    // window.location.pathname as the unresolved page template (e.g.
    // `/scribe/session/[...id]`). A `[` is never valid in a resolved URL
    // path, so bail out to avoid clobbering the URL with the raw template.
    if (window.location.pathname.includes('[')) return;

    const newUrl = `${window.location.pathname}${window.location.search}${
      searchParams ? `#${searchParams}` : ''
    }`;
    if (setHash === 'replaceState') {
      window.history.replaceState(window.history.state, '', newUrl);
    } else {
      window.history.pushState(window.history.state, '', newUrl);
    }
  };
